import { Typography } from '@mui/joy';
import { CloudArrowUp } from '@phosphor-icons/react';
import { CSSProperties, useCallback } from 'react';
import { Accept, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import { Color, Level } from 'theme/enums';

import { Wrapper } from './style';

interface IProps {
  accept?: Accept;
  maxFiles?: number;
  onUpload?: (acceptedFiles: File[]) => void;
  style?: CSSProperties;
}

const FileDropzone = ({ accept, maxFiles = 1, onUpload, style }: IProps) => {
  const { t } = useTranslation();

  const onDrop = useCallback(
    (acceptedFiles?: File[]) => {
      if (!acceptedFiles?.length) {
        return;
      }

      onUpload && onUpload(acceptedFiles);
    },
    [onUpload]
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    maxFiles,
    multiple: maxFiles > 1,
    ...(accept && { accept }),
  });

  const acceptedFileTypes = accept && Object.values(accept).flat();

  return (
    <Wrapper {...getRootProps()} isDragReject={isDragReject} sx={style}>
      <>
        <input {...getInputProps()} />

        {!isDragReject && (
          <Typography
            level={Level.TITLE_MD}
            color={Color.PRIMARY}
            sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <CloudArrowUp />

            {t(!isDragActive ? 'messageDragFiles' : 'messageDropFiles', {
              count: maxFiles,
            })}
          </Typography>
        )}

        {isDragReject && (
          <Typography level={Level.TITLE_MD} color={Color.DANGER}>
            {t('messageDropFilesReject')}
          </Typography>
        )}

        {acceptedFileTypes && (
          <Typography color={Color.NEUTRAL} level={Level.TITLE_SM}>
            {t('messageSupportedFileTypes', {
              types: acceptedFileTypes.join(', '),
            })}
          </Typography>
        )}
      </>
    </Wrapper>
  );
};

export default FileDropzone;
